/**
 * A polygon's grid: cells of a set size laid over the shape, turned to a
 * bearing and anchored at a point -- the GRID of the .mmp format, which the
 * mobile apps have always written, and what a grid is used for: counting the
 * tiles, panels, plots or planting positions that fit.
 *
 * Geometry lives in utils/grid-geometry.ts; this is only the setting.
 */
export interface PolygonGrid {
  /** Metres, along the grid's first axis (east-west at bearing 0). */
  cellWidth: number;
  /** Metres, along the second axis (north-south at bearing 0). */
  cellHeight: number;
  /** Degrees the grid is turned clockwise, 0 up to 360. */
  bearing: number;
  /** A corner of the grid: every line passes a whole number of cells from it. */
  refLat: number;
  refLng: number;
  /** Drawn on the map. The setting is kept while it is hidden. */
  show: boolean;
  /** The lines' colour, `#rrggbb`; none (null) draws them in the shape's line colour. */
  lineColor?: string | null;
  /** The lines' thickness, in pixels on the map. */
  lineWidth?: number;
  /**
   * A dot at the centre of each cell whose centre is inside the shape: where
   * a tree, a post or a plant goes when the grid is a planting plan.
   */
  showCentres?: boolean;
}

/** Smallest cell a grid may have, in metres: below it nothing useful can be drawn. */
export const GRID_MIN_CELL_METERS = 0.01;

/** How thick the lines may be drawn, in pixels, and how thick they are by default. */
export const GRID_LINE_WIDTH = {min: 0.5, max: 6, step: 0.5, initial: 1};

/** As stored on the polygon document (snake case, like its other fields); null clears it. */
export function gridToData(grid: PolygonGrid | null | undefined): any {
  const g = normalizeGrid(grid);
  if (!g) {
    return null;
  }
  return {
    cell_width: g.cellWidth,
    cell_height: g.cellHeight,
    bearing: g.bearing,
    ref_lat: g.refLat,
    ref_lng: g.refLng,
    show: g.show,
    line_color: g.lineColor,
    line_width: g.lineWidth,
    show_centres: g.showCentres,
  };
}

export function gridFromData(data: any): PolygonGrid | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  return normalizeGrid({
    cellWidth: data.cell_width,
    cellHeight: data.cell_height,
    bearing: data.bearing,
    refLat: data.ref_lat,
    refLng: data.ref_lng,
    show: data.show !== false,
    lineColor: data.line_color,
    lineWidth: data.line_width,
    showCentres: data.show_centres,
  });
}

/** A grid that can be drawn, or null: sizes positive, the anchor a real place. */
export function normalizeGrid(grid: Partial<PolygonGrid> | null | undefined): PolygonGrid | null {
  if (!grid) {
    return null;
  }
  const finite = (n: any) => typeof n === 'number' && isFinite(n);
  if (!finite(grid.cellWidth) || !finite(grid.cellHeight) || !finite(grid.refLat) || !finite(grid.refLng)) {
    return null;
  }
  if (grid.cellWidth < GRID_MIN_CELL_METERS || grid.cellHeight < GRID_MIN_CELL_METERS
    || Math.abs(grid.refLat) > 90 || Math.abs(grid.refLng) > 180) {
    return null;
  }
  const bearing = finite(grid.bearing) ? ((grid.bearing % 360) + 360) % 360 : 0;
  // A grid saved before these existed, or by an app that does not know them,
  // draws as it always did: thin lines in the shape's colour, with the dots.
  const color = typeof grid.lineColor === 'string' && /^#[0-9a-f]{6}$/i.test(grid.lineColor)
    ? grid.lineColor.toLowerCase() : null;
  const width = finite(grid.lineWidth)
    ? Math.min(GRID_LINE_WIDTH.max, Math.max(GRID_LINE_WIDTH.min, grid.lineWidth)) : GRID_LINE_WIDTH.initial;
  return {
    cellWidth: grid.cellWidth,
    cellHeight: grid.cellHeight,
    bearing,
    refLat: grid.refLat,
    refLng: grid.refLng,
    show: grid.show !== false,
    lineColor: color,
    lineWidth: width,
    showCentres: grid.showCentres !== false,
  };
}
